import { Subject } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { WORKSPACE_LIMIT_CLOSE_CODE } from './syncErrorUtils'
import type { Observable} from 'rxjs';
import { getAccessToken } from '@/lib/auth/authSession'
import { ENDPOINT_POST_GET_CONNECT_TOKEN } from '@/lib/api/features/authentication/config/endpoints'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'
import useLoginStore from '@/store/LoginStore'

/**
 * Fired when moly-sync rejects the WebSocket because the user has the maximum
 * number of concurrent workspace sessions open elsewhere. UI listens for this
 * via window.addEventListener to show a blocking modal.
 */
export const WORKSPACE_LIMIT_EVENT = 'moly:workspace-limit-exceeded'

/**
 * Mirror a workspace broadcast (name, slug, logo, description, mode, migration
 * flag) into LoginStore so other tabs and devices reflect updates without a
 * refresh. Exported for unit testing.
 */
export function applyWorkspaceBroadcast(message: any) {
  const doc = message?.document
  if (!doc || typeof doc !== 'object') return

  const broadcastWorkspaceId: string | undefined = doc.id ?? doc.Id
  if (!broadcastWorkspaceId) return

  const store = useLoginStore.getState()
  const target = store.workspaces.find((w) => w.workspaceId === broadcastWorkspaceId)
  if (!target) return

  const next = {
    ...target,
    name: doc.name ?? target.name,
    workspaceSlug: doc.workspaceSlug ?? target.workspaceSlug,
    logoUrl: doc.logoUrl ?? null,
    description: doc.description ?? null,
    workspaceType:
      typeof doc.workspaceType === 'number' ? doc.workspaceType : target.workspaceType,
    statusMigrationInProgress:
      typeof doc.statusMigrationInProgress === 'boolean'
        ? doc.statusMigrationInProgress
        : target.statusMigrationInProgress,
    // AI feature kill-switch propagation — backend BroadcastWorkspaceChangeAsync
    // includes `aiEnabled`, so the toggle reaches clients without a refetch.
    aiEnabled:
      typeof doc.aiEnabled === 'boolean' ? doc.aiEnabled : target.aiEnabled,
  }

  store.setWorkspaces(
    store.workspaces.map((w) => (w.workspaceId === broadcastWorkspaceId ? next : w)),
  )

  if (store.workspaceId === broadcastWorkspaceId) {
    if (next.workspaceType !== store.workspaceType) {
      store.setWorkspaceType(next.workspaceType)
    }
    if (next.workspaceSlug !== store.workspaceSlug) {
      store.setWorkspaceSlug(next.workspaceSlug)
    }
  }
}

/**
 * Shared WebSocket Manager
 * Creates ONE WebSocket connection shared by ALL collections
 */
class SharedWebSocketManager {
  private ws: WebSocket | null = null
  private subject: Subject<any> | null = null
  private sharedStream$: Observable<any> | null = null
  private reconnectAttempts = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private url = ''
  private isConnecting = false
  private workspaceLimitBlocked = false

  /**
   * Get or create the shared WebSocket stream
   */
  getStream(url: string): Observable<any> {
    if (this.sharedStream$ && this.url === url) {
      return this.sharedStream$
    }

    this.url = url
    this.subject = new Subject()

    // Return the subject directly as Observable (it IS an Observable)
    // Don't use .pipe(share()) - the Subject is already multicast
    this.sharedStream$ = this.subject.asObservable()

    // Connect (async, doesn't block)
    void this.connect().catch((error) => {
      logger.logError(error as Error, LogCategory.WEBSOCKET, {
        phase: 'async_connection',
      })
    })

    return this.sharedStream$
  }

  private async connect() {
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      return
    }

    if (this.workspaceLimitBlocked) {
      // User has hit the concurrent-workspace cap; wait for explicit retry().
      return
    }

    const token = getAccessToken()
    if (!token) {
      logger.warn('WebSocket connection failed: No access token', {
        category: LogCategory.WEBSOCKET,
      })
      return
    }

    this.isConnecting = true
    logger.logEvent(LogCategory.WEBSOCKET, 'connect.attempt', {
      url: this.url,
      reconnectAttempt: this.reconnectAttempts,
    })

    const connectToken = await this.requestConnectToken(token)
    if (!connectToken) {
      this.isConnecting = false
      logger.logError(
        new Error('Failed to obtain WebSocket connect token'),
        LogCategory.WEBSOCKET
      )
      return
    }

    const wsUrl = `${this.url}?token=${encodeURIComponent(connectToken)}`

    try {
      this.ws = new WebSocket(wsUrl)
    } catch (error) {
      this.isConnecting = false
      logger.logError(error as Error, LogCategory.WEBSOCKET, {
        phase: 'connection',
      })
      throw error
    }

    this.ws.onopen = () => {
      this.isConnecting = false
      this.reconnectAttempts = 0

      logger.logEvent(LogCategory.WEBSOCKET, 'connect.success', {
        url: this.url,
      })

      // Emit RESYNC for all collections
      this.subject?.next('RESYNC')

      // Send ping
      if (this.ws) {
        this.ws.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }))
      }

      // Start heartbeat
      this.startHeartbeat()
    }

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data)

        // Emit to all subscribers
        if (message.type === 'document_changed' || message.type === 'document_deleted') {
          this.subject?.next(message)

          // Handle workspace setting changes for all connected clients
          if (message.collection === 'workspaces' && message.type === 'document_changed') {
            this.handleWorkspaceChange(message)
          }
        }
      } catch (error) {
        logger.logError(
          new Error('WebSocket message parse error'),
          LogCategory.WEBSOCKET,
          {
            rawMessage: event.data,
          }
        )
      }
    }

    // The error event carries no actionable payload and is always followed by
    // onclose (which logs the close code/reason and drives reconnection), so
    // logging at error level just floods Sentry with normal network blips.
    // Record an info breadcrumb for context; onclose owns the real log.
    this.ws.onerror = (event) => {
      this.isConnecting = false
      logger.logEvent(LogCategory.WEBSOCKET, 'error_event', {
        event: event.type,
      })
    }

    this.ws.onclose = (event) => {
      this.isConnecting = false
      this.stopHeartbeat()

      logger.logEvent(LogCategory.WEBSOCKET, 'disconnect', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      })

      // Server rejected because the user has hit the concurrent-workspace cap.
      // Stop reconnecting and surface a blocking UX via a custom event.
      if (event.code === WORKSPACE_LIMIT_CLOSE_CODE) {
        this.workspaceLimitBlocked = true
        this.emitWorkspaceLimit(event.reason || 'workspace_limit_exceeded')
        this.subject?.next({
          type: 'sync_error',
          error: new Error(`workspace_limit_exceeded: ${event.reason}`),
        })
        return
      }

      // Reconnect
      if (this.reconnectAttempts < 10) {
        this.reconnectAttempts++
        const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000)

        logger.logEvent(LogCategory.WEBSOCKET, 'reconnect.scheduled', {
          attempt: this.reconnectAttempts,
          delayMs: delay,
        })

        this.reconnectTimer = setTimeout(() => this.connect(), delay)
      } else {
        logger.logError(
          new Error('WebSocket max reconnection attempts reached'),
          LogCategory.WEBSOCKET,
          {
            maxAttempts: 10,
          }
        )
      }
    }
  }

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null

  private startHeartbeat() {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }))
      }
    }, 30000)
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  /**
   * Handle workspace broadcast messages.
   * Status management settings are project-level and flow through project
   * replication, so they are intentionally ignored here.
   */
  private handleWorkspaceChange(message: any) {
    applyWorkspaceBroadcast(message)
  }

  private emitWorkspaceLimit(reason: string) {
    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent(WORKSPACE_LIMIT_EVENT, { detail: { reason } })
        )
      }
    } catch (error) {
      logger.logError(error as Error, LogCategory.WEBSOCKET, {
        phase: 'workspace_limit_emit',
      })
    }
  }

  /**
   * Called from the UI modal after the user confirms they've closed another tab.
   * Resets the block and re-attempts the connection.
   */
  retryAfterWorkspaceLimit() {
    this.workspaceLimitBlocked = false
    this.reconnectAttempts = 0
    void this.connect()
  }

  private async requestConnectToken(accessToken: string): Promise<string | null> {
    try {
      const workspaceId = useLoginStore.getState().workspaceId
      const params = new URLSearchParams({ clientType: 'browser' })
      if (workspaceId) params.set('workspaceId', workspaceId)
      const url = `${ENDPOINT_POST_GET_CONNECT_TOKEN}?${params.toString()}`
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })

      if (!response.ok) {
        return null
      }

      const data = await response.json()
      if (!data?.token) {
        return null
      }

      return String(data.token)
    } catch (error) {
      logger.logError(error as Error, LogCategory.WEBSOCKET, {
        phase: 'token_request',
      })
      return null
    }
  }

  /**
   * Close the shared WebSocket connection
   */
  close() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
    }

    this.stopHeartbeat()

    if (this.ws) {
      this.ws.close()
      this.ws = null
    }

    this.subject?.complete()
    this.subject = null
    this.sharedStream$ = null
  }
}

// Singleton instance
const manager = new SharedWebSocketManager()

/**
 * Get the shared WebSocket stream for a specific collection
 */
export function getSharedWebSocketStream(
  collectionName: string,
  url: string
): { stream$: Observable<any>; close: () => void } {
  // Get the shared stream
  const sharedStream$ = manager.getStream(url)

  // Filter for this collection only
  const collectionStream$ = sharedStream$.pipe(
    map((msg) => {
      // Pass RESYNC to all collections
      if (msg === 'RESYNC') {
        return 'RESYNC'
      }

      // Filter by collection
      if (
        msg &&
        typeof msg === 'object' &&
        msg.collection === collectionName &&
        (msg.type === 'document_changed' || msg.type === 'document_deleted')
      ) {
        return msg
      }

      // Ignore messages for other collections
      return null
    }),
    filter((x) => x !== null)
  )

  return {
    stream$: collectionStream$,
    close: () => {
      // Don't close the shared WebSocket when one collection unsubscribes
    },
  }
}

/**
 * Close the shared WebSocket (call when database closes)
 */
export function closeSharedWebSocket() {
  manager.close()
}

/**
 * Re-attempt the WebSocket connection after the user resolves a
 * workspace-limit rejection (e.g., closed another tab).
 */
export function retryAfterWorkspaceLimit() {
  manager.retryAfterWorkspaceLimit()
}
